import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { worksheetAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CurriculumPicker from '@/components/CurriculumPicker';
import { Home, ArrowLeft, Upload, Sparkles, Save, Download, Trash2, Loader2, Plus, Minus, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BOARDS = ['NCERT', 'CBSE', 'ICSE', 'IB', 'Cambridge', 'State Board', 'Other'];
const LANGUAGES = [
  { v: 'en', l: 'English (default)' },
  { v: 'hi', l: 'हिन्दी (Hindi)' },
  { v: 'sa', l: 'संस्कृतम् (Sanskrit)' },
  { v: 'fr', l: 'Français (French)' },
  { v: 'es', l: 'Español (Spanish)' },
  { v: 'zh', l: '中文 (Mandarin)' },
];

const WorksheetBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadingChapter, setUploadingChapter] = useState(false);
  const [aiProvider, setAiProvider] = useState('openai');
  const [questionTypes, setQuestionTypes] = useState([]);
  const chapterFileRef = useRef(null);
  const [form, setForm] = useState({
    class_name: '',
    subject: '',
    chapter: '',
    board: '',
    book_name: '',
    language: user?.preferred_language || 'en',
    num_questions: 10,
    max_marks: 20,
    question_specs: [],
    questions: [],
    source_chapter_filename: '',
    is_draft: true,
  });
  const [chapterText, setChapterText] = useState('');
  const [libScope, setLibScope] = useState({ book_ids: [], chapter_ids: [], topic_ids: [], scope_summary: '', filters: {} });

  // Auto-fill class/subject from picker if empty
  useEffect(() => {
    if (libScope.filters?.class_name && !form.class_name) setForm((f) => ({ ...f, class_name: libScope.filters.class_name }));
    if (libScope.filters?.subject && !form.subject) setForm((f) => ({ ...f, subject: libScope.filters.subject }));
    if (libScope.filters?.board && !form.board) setForm((f) => ({ ...f, board: libScope.filters.board }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libScope.filters]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await worksheetAPI.questionTypes();
        setQuestionTypes(data.types || []);
      } catch { /* ignore */ }
      if (id) {
        setLoading(true);
        try {
          const { data } = await worksheetAPI.getOne(id);
          setForm(data);
        } catch {
          toast.error('Failed to load worksheet');
          navigate('/dashboard');
        } finally { setLoading(false); }
      }
    })();
  }, [id, navigate]);

  const totalSelected = form.question_specs.reduce((s, x) => s + (x.count || 0), 0);

  const setSpecCount = (type, count) => {
    const existing = form.question_specs.find((s) => s.type === type);
    let next;
    if (existing) {
      next = form.question_specs.map((s) => s.type === type ? { ...s, count: Math.max(0, count) } : s);
    } else {
      next = [...form.question_specs, { type, count: Math.max(0, count) }];
    }
    next = next.filter((s) => s.count > 0);
    setForm({ ...form, question_specs: next });
  };

  const specCount = (type) => form.question_specs.find((s) => s.type === type)?.count || 0;

  const handleChapterUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > 5) {
      toast.error('Please upload at most 5 files');
      return;
    }
    setUploadingChapter(true);
    try {
      let combinedText = '';
      let filenames = [];
      if (files.length === 1) {
        const singleName = files[0].name.toLowerCase();
        const isImage = /\.(png|jpg|jpeg|webp)$/.test(singleName);
        if (isImage) {
          const { data } = await worksheetAPI.uploadChapters(files);
          combinedText = data.chapter_text || '';
          filenames = data.files?.filter((f) => f.ok).map((f) => f.filename) || [];
        } else {
          const { data } = await worksheetAPI.uploadChapter(files[0]);
          combinedText = data.chapter_text || '';
          filenames = [data.filename];
        }
      } else {
        const { data } = await worksheetAPI.uploadChapters(files);
        combinedText = data.chapter_text || '';
        filenames = data.files?.filter((f) => f.ok).map((f) => f.filename) || [];
        const failed = data.files?.filter((f) => !f.ok) || [];
        failed.forEach((f) => toast.error(`${f.filename}: ${f.reason}`));
      }
      setChapterText(combinedText);
      setForm({ ...form, source_chapter_filename: filenames.join(', ') });
      toast.success(`${filenames.length} file(s) loaded (${combinedText.length.toLocaleString()} chars)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chapter upload failed');
    } finally {
      setUploadingChapter(false);
      if (chapterFileRef.current) chapterFileRef.current.value = '';
    }
  };

  const canGenerate = form.class_name && form.subject && (form.chapter || libScope.chapter_ids.length || libScope.book_ids.length) && totalSelected > 0;

  const handleGenerate = async () => {
    if (!canGenerate) {
      toast.error('Fill Class, Subject, pick a Chapter/Topic (or type one), and set at least one question type');
      return;
    }
    setGenerating(true);
    try {
      const { data } = await worksheetAPI.generate({
        subject: form.subject,
        class_name: form.class_name,
        chapter: form.chapter || libScope.scope_summary || 'Selected chapters',
        board: form.board || null,
        book_name: form.book_name || null,
        language: form.language,
        num_questions: totalSelected,
        max_marks: form.max_marks,
        question_specs: form.question_specs,
        chapter_text: chapterText || null,
        provider: aiProvider,
        model: aiProvider === 'anthropic' ? 'claude-haiku-4-5-20251001' : null,
        library_book_ids: libScope.book_ids,
        library_chapter_ids: libScope.chapter_ids,
        library_topic_ids: libScope.topic_ids,
      });
      setForm((f) => ({ ...f, num_questions: totalSelected, questions: data.questions || [] }));
      toast.success(`Generated ${(data.questions || []).length} questions${data.curriculum_scope_summary ? ' (scope-locked)' : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const handleSave = async (isDraft) => {
    if (!form.class_name || !form.subject || !form.chapter) {
      toast.error('Class, Subject, and Topic are required');
      return;
    }
    if (!isDraft && (!form.questions || form.questions.length === 0)) {
      toast.error('Generate at least one question before finalizing');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, num_questions: form.questions.length || form.num_questions, is_draft: isDraft };
      if (id) {
        const { data } = await worksheetAPI.update(id, payload);
        setForm(data);
      } else {
        const { data } = await worksheetAPI.create(payload);
        toast.success(isDraft ? 'Draft saved' : 'Worksheet finalized!');
        navigate(`/worksheet/${data.worksheet_id}`);
        return;
      }
      toast.success(isDraft ? 'Draft saved' : 'Worksheet finalized!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const download = async (fmt) => {
    if (!id) { toast.error('Save the worksheet first'); return; }
    try {
      const api = fmt === 'docx' ? worksheetAPI.downloadDocx : worksheetAPI.downloadPdf;
      const { data } = await api(id, true);
      const blob = new Blob([data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `worksheet.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error('Download failed');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C53030]" /></div>;

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button data-testid="home-button" variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#C53030]"><Home className="w-5 h-5 mr-2" /> Home</Button>
            <Button data-testid="back-button" variant="ghost" onClick={() => navigate('/dashboard')}><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="text-2xl font-bold text-[#1C1917]">{id ? 'Edit' : 'Create'} Worksheet</h1>
          </div>
          <div className="flex gap-2">
            <Button data-testid="ws-generate-btn" onClick={handleGenerate} disabled={generating || !canGenerate} className="bg-gradient-to-r from-[#C53030] to-[#9B2C2C] text-white">
              <Sparkles className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} /> {generating ? 'Generating...' : 'AI Generate'}
            </Button>
            <Select value={aiProvider} onValueChange={setAiProvider}>
              <SelectTrigger data-testid="ws-ai-provider" className="w-32 h-9 border border-stone-200 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="anthropic">Claude</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="ws-save-draft" variant="outline" onClick={() => handleSave(true)} disabled={saving} className="border border-stone-200"><Save className="w-4 h-4 mr-2" /> Save Draft</Button>
            <Button data-testid="ws-finalize" onClick={() => handleSave(false)} disabled={saving} className="bg-[#C53030] text-white">Finalize</Button>
            {id && <Button data-testid="ws-download-pdf" variant="outline" onClick={() => download('pdf')} className="border border-stone-200"><Download className="w-4 h-4 mr-2" /> PDF</Button>}
            {id && <Button data-testid="ws-download-docx" variant="outline" onClick={() => download('docx')} className="border border-stone-200"><Download className="w-4 h-4 mr-2" /> DOCX</Button>}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-3">
          <CurriculumPicker onChange={setLibScope} defaultClass={form.class_name} defaultSubject={form.subject} defaultBoard={form.board || 'CBSE'} />
        </div>
        <div className="lg:col-span-1 space-y-6">
          <Card className="p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-[#1C1917] mb-4">Worksheet Details</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Class</Label><Input data-testid="ws-class-input" value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })} placeholder="Grade 8" className="mt-1" /></div>
                <div><Label>Subject</Label><Input data-testid="ws-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Mathematics" className="mt-1" /></div>
              </div>
              <div>
                <Label>Topic / Chapter</Label>
                <Input
                  data-testid="ws-chapter-input"
                  value={form.chapter}
                  onChange={(e) => setForm({ ...form, chapter: e.target.value })}
                  placeholder="Pick chapters from the Curriculum Library above, or type your own"
                  className="mt-1"
                  autoComplete="off"
                />
                {libScope.chapter_ids?.length > 0 && (
                  <p className="text-xs text-emerald-700 mt-1" data-testid="ws-curriculum-hint">
                    📚 {libScope.chapter_ids.length} chapter(s) selected from Curriculum Library
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Board</Label>
                  <Select value={form.board || ''} onValueChange={(v) => setForm({ ...form, board: v })}>
                    <SelectTrigger data-testid="ws-board-select" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{BOARDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Book Name</Label><Input data-testid="ws-book-input" value={form.book_name || ''} onChange={(e) => setForm({ ...form, book_name: e.target.value })} placeholder="Optional" className="mt-1" /></div>
              </div>
              <div>
                <Label>Language</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger data-testid="ws-language-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Max Marks</Label><Input data-testid="ws-max-marks" type="number" min={1} max={500} value={form.max_marks} onChange={(e) => setForm({ ...form, max_marks: parseInt(e.target.value, 10) || 0 })} className="mt-1" /></div>
                <div><Label>Total Questions</Label><Input data-testid="ws-num-questions" type="number" value={totalSelected} readOnly className="mt-1 bg-stone-50" /></div>
              </div>
              <div className="pt-2 border-t border-stone-200">
                <Label>Upload Chapter Files <span className="text-stone-500 text-xs">(optional — PDF, DOCX, TXT, or up to 5 photos; AI grounds questions in this content)</span></Label>
                <input ref={chapterFileRef} data-testid="ws-chapter-file-input" type="file" multiple accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,.webp" onChange={handleChapterUpload} className="hidden" />
                <Button data-testid="ws-chapter-upload-btn" type="button" variant="outline" onClick={() => chapterFileRef.current?.click()} disabled={uploadingChapter} className="w-full mt-2 border border-stone-200">
                  {uploadingChapter ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {form.source_chapter_filename ? form.source_chapter_filename : 'Upload PDF / DOCX / TXT / Photos (up to 5)'}
                </Button>
                {chapterText && <p className="text-xs text-emerald-700 mt-2">{chapterText.length.toLocaleString()} characters loaded</p>}
              </div>
            </div>
          </Card>

          <Card className="p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-[#1C1917] mb-1">Question Types & Counts</h2>
            <p className="text-xs text-stone-500 mb-4">Set the number of questions per type. Total: <strong>{totalSelected}</strong></p>
            <div className="space-y-2">
              {questionTypes.map((qt) => {
                const count = specCount(qt.key);
                return (
                  <div key={qt.key} data-testid={`ws-type-row-${qt.key}`} className="flex items-center justify-between border border-stone-200 rounded-md p-2">
                    <span className="text-sm text-[#1C1917] flex-1 pr-2">{qt.label}</span>
                    <div className="flex items-center gap-1">
                      <Button data-testid={`ws-type-dec-${qt.key}`} size="icon" variant="outline" onClick={() => setSpecCount(qt.key, count - 1)} disabled={count <= 0} className="w-7 h-7 border border-stone-200"><Minus className="w-3 h-3" /></Button>
                      <span data-testid={`ws-type-count-${qt.key}`} className="w-8 text-center text-sm font-semibold">{count}</span>
                      <Button data-testid={`ws-type-inc-${qt.key}`} size="icon" variant="outline" onClick={() => setSpecCount(qt.key, count + 1)} disabled={count >= 50} className="w-7 h-7 border border-stone-200"><Plus className="w-3 h-3" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card className="p-6 border border-stone-200 min-h-[600px]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-[#1C1917]">Preview & Edit Questions</h2>
              <span className="text-sm text-stone-500">{form.questions.length} question{form.questions.length !== 1 ? 's' : ''}</span>
            </div>
            {form.questions.length === 0 ? (
              <div className="text-center py-16">
                <FileText className="w-12 h-12 mx-auto text-stone-300 mb-3" />
                <p className="text-stone-500">Set question counts and click <strong>AI Generate</strong> to create the worksheet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {form.questions.map((q, i) => (
                  <div key={`q-${i}`} data-testid={`ws-question-${i}`} className="border border-stone-200 rounded-md p-4">
                    <div className="flex justify-between items-start gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs uppercase font-semibold text-[#C53030] bg-red-50 px-2 py-0.5 rounded">Q{i + 1}</span>
                        <span className="text-xs text-stone-500">{q.type}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input type="number" value={q.marks || 1} onChange={(e) => { const v = parseInt(e.target.value, 10) || 1; const next = [...form.questions]; next[i] = { ...next[i], marks: v }; setForm({ ...form, questions: next }); }} className="w-16 h-7 text-xs" />
                        <span className="text-xs text-stone-500">marks</span>
                        <Button data-testid={`ws-delete-q-${i}`} size="icon" variant="ghost" onClick={() => setForm({ ...form, questions: form.questions.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                    <textarea className="w-full text-sm text-[#1C1917] border border-stone-200 rounded p-2 whitespace-pre-line" rows={q.type === 'seen_passage' || q.type === 'unseen_passage' ? 6 : 3} value={q.question || ''} onChange={(e) => { const next = [...form.questions]; next[i] = { ...next[i], question: e.target.value }; setForm({ ...form, questions: next }); }} />
                    {q.options?.length ? (
                      <div className="mt-2 space-y-1">
                        {q.options.map((op, k) => <p key={`op-${i}-${k}`} className="text-xs text-stone-700 pl-4">{op}</p>)}
                      </div>
                    ) : null}
                    {q.answer && (
                      <details className="mt-2">
                        <summary className="text-xs text-emerald-700 cursor-pointer">Show answer</summary>
                        <textarea className="w-full text-xs text-stone-700 border border-stone-200 rounded p-2 mt-1 whitespace-pre-line" rows={2} value={q.answer} onChange={(e) => { const next = [...form.questions]; next[i] = { ...next[i], answer: e.target.value }; setForm({ ...form, questions: next }); }} />
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
};

export default WorksheetBuilder;
